/* eslint-disable @next/next/no-img-element */
import { FC, ReactNode, useCallback } from 'react';

import { RiArrowDropRightLine } from 'react-icons/ri';
import { IconType } from 'react-icons';

import { TColorProp, useTheme } from 'styled-components';

import { useClickOutside } from '~/common/hooks/use-click-outside';
import { Typography } from '~/common/components/Typograph';

import {
  Container,
  ListContent,
  ListWrapper,
  NoOptionsWrapper,
  Option,
  OptionContent,
  OptionLabelSublabelWrapper,
} from './styles';
import {
  TDropdownPosition,
  TDropdownSize,
  IDropdownOption,
  TDropdownValue,
  TDropdownVariant,
  TBorderRadius,
} from '../types';

interface IDropdownOptionsProps {
  inputWrapperClientRect: DOMRect;
  containerClientRect: DOMRect;
  position: TDropdownPosition;
  isDropdownVisible: boolean;
  size: TDropdownSize;
  borderRadius?: TBorderRadius;
  noOptionsText: string;
  options: IDropdownOption[];
  isDisabled: boolean;
  onCloseDropdown: () => void;
  onSelectOption: (option: TDropdownValue) => void;
  leftIcon: IconType;
  leftCustomIcon: ReactNode;
  variant: TDropdownVariant;
  selectedOption: IDropdownOption;
  backgroundColor?: TColorProp;
  optionHoverBackgroundColor?: TColorProp;
  borderColor?: TColorProp;
  optionTextColor?: TColorProp;
  selectedOptionBackgroundColor?: TColorProp;
}

export const DropdownOptions: FC<IDropdownOptionsProps> = ({
  isDropdownVisible,
  size,
  borderRadius = 'default',
  noOptionsText,
  options = [],
  isDisabled,
  onCloseDropdown,
  onSelectOption,
  leftIcon: LeftIcon,
  containerClientRect,
  inputWrapperClientRect,
  position,
  variant,
  selectedOption,
  backgroundColor = 'neutral950',
  optionHoverBackgroundColor = 'neutral900',
  borderColor = 'neutral200',
  optionTextColor = 'neutral600',
  selectedOptionBackgroundColor = 'neutral50',
}) => {
  const theme = useTheme();
  const contentRef = useClickOutside<HTMLDivElement>(onCloseDropdown);

  const handleSelectOption = useCallback(
    async (option: IDropdownOption): Promise<void> => {
      const value = option?.value || '';

      onSelectOption(value);
      onCloseDropdown();
    },
    [onCloseDropdown, onSelectOption]
  );

  return (
    <>
      <Container
        $isVisible={isDropdownVisible}
        $containerClientRect={containerClientRect}
        $position={position}
      />

      <ListWrapper
        $isVisible={isDropdownVisible}
        $inputWrapperClientRect={inputWrapperClientRect}
        $position={position}
      >
        <ListContent
          ref={contentRef}
          $backgroundColor={backgroundColor}
          $isVisible={isDropdownVisible}
          $position={borderRadius === 'full' ? borderRadius : position}
          $borderColor={borderColor}
        >
          {options?.length > 0 ? (
            <ul>
              {options.map((option) => {
                const optionIsDisabled = option.isDisabled || isDisabled;
                const optionIsSelected = option.value === selectedOption?.value;
                const showLabel = !!option.label && !option.hideLabel;

                return (
                  <Option
                    $size={size}
                    $selectedBackgroundColor={selectedOptionBackgroundColor}
                    $optionHoverBackgroundColor={optionHoverBackgroundColor}
                    $backgroundColor={backgroundColor}
                    key={String(option.value)}
                    id={`dropdown-option-${option.value}`}
                    $isDisabled={optionIsDisabled}
                    {...(!optionIsDisabled && {
                      onClick: () => handleSelectOption(option),
                    })}
                    $variant={variant}
                    $isSelected={optionIsSelected}
                    {...(!showLabel && {
                      style: {
                        justifyContent: 'center',
                      },
                    })}
                  >
                    {option.image && (
                      <img
                        src={
                          typeof option.image === 'string'
                            ? option.image
                            : option.image?.src
                        }
                        alt={option.label}
                        style={{
                          width: '30px',
                          height: '30px',
                        }}
                      />
                    )}

                    {option.leftCustomIcon && <>{option.leftCustomIcon}</>}

                    {LeftIcon && (
                      <LeftIcon size={40} color={theme[optionTextColor]} />
                    )}

                    {showLabel && (
                      <OptionContent>
                        <header>
                          <OptionLabelSublabelWrapper>
                            <Typography.Paragraph
                              size="small"
                              color={optionTextColor}
                            >
                              {option.label}
                            </Typography.Paragraph>

                            {option.sublabel && (
                              <Typography.Paragraph
                                size="x-small"
                                color={optionTextColor}
                              >
                                {option.sublabel}
                              </Typography.Paragraph>
                            )}
                          </OptionLabelSublabelWrapper>
                        </header>

                        {option.description && (
                          <Typography.Paragraph
                            size="x-small"
                            color="neutral400"
                          >
                            {option.description}
                          </Typography.Paragraph>
                        )}
                      </OptionContent>
                    )}

                    {variant !== 'compact' && (
                      <RiArrowDropRightLine
                        size={20}
                        color={theme.neutral400}
                      />
                    )}
                  </Option>
                );
              })}
            </ul>
          ) : (
            <NoOptionsWrapper $size={size} id="select-option-empty">
              <Typography.Paragraph
                size={variant === 'compact' ? 'x-small' : 'small'}
                color="neutral500"
              >
                {noOptionsText || 'Sem opções!'}
              </Typography.Paragraph>
            </NoOptionsWrapper>
          )}
        </ListContent>
      </ListWrapper>
    </>
  );
};
